import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { green, red, amber, blue } from '@mui/material/colors';
import { ApiInstance } from '../../../api/api';
import { DeviceSwitchPortsDataType } from '../../../types/buildingTypes';
import {
  PortActionDialogState,
  PortDataType,
  MerakiPortDataType,
  PerformPortBounceRequestType,
  PerformMerakiPortBounceRequestType,
  PortActionSuccess,
  PortActionFailure,
  PortActionBatchResponse,
} from '../../../types/types';
import IconButton from '@mui/material/IconButton';
import PortBounceForm from './PortBounceForm';
import DialogContentLoading from '../common/DialogContentLoading';
import DialogContentSuccess from '../common/DialogContentSuccess';
import DialogContentError from '../common/DialogContentError';
import dialogStyles from '../common/dialogStyles';
import { PortActionError, portActionErrorDesc } from '../../../constants/errorCodes';
import { AxiosResponse } from 'axios';
import DialogContentBatchResult from '../common/DialogContentBatchResult';
import { parseApiError } from '../../../utils/apiErrorHandler';

interface PortBounceActionProps {
  open: boolean;
  onClose: () => void;
  unlimitedPortBounce: boolean;
  ip_address: string | undefined;
  device_type: string | null;
  region: string | null;
  building_code: string | null;
  unit: string | undefined;
  unit_name: string | null;
  selectedPorts: DeviceSwitchPortsDataType[];
  isJuniper: boolean;
  isMeraki: boolean;
  isApiMode: boolean;
  isDistro?: boolean;
}

const PortBounceAction: React.FC<PortBounceActionProps> = ({
  open,
  onClose,
  unlimitedPortBounce,
  ip_address,
  device_type,
  region,
  building_code,
  unit,
  unit_name,
  selectedPorts,
  isJuniper,
  isMeraki,
  isApiMode,
  isDistro = false,
}) => {
  const DEFAULT_LOADING_TEXT = 'Please wait while we process your request...';
  const DEFAULT_ERROR_TEXT = 'Something went wrong. Please try again later.';

  const titleMap: Record<PortActionDialogState, string> = {
    default: `Are you sure you want to bounce the following port${selectedPorts.length !== 1 ? 's' : ''}?`,
    loading: 'Bounce action',
    success: 'Bounce action',
    partial: 'Bounce action',
    error: 'Bounce action',
  };

  const backgroundColorMap: Record<PortActionDialogState, string> = {
    default: amber[800],
    loading: blue[600],
    success: green[500],
    partial: amber[800],
    error: red[500],
  };

  const [dialogState, setDialogState] = useState<PortActionDialogState>('default');
  const [loadingText, setLoadingText] = useState(DEFAULT_LOADING_TEXT);
  const [errorText, setErrorText] = useState(DEFAULT_ERROR_TEXT);
  const [reloadOnClose, setReloadOnClose] = useState<Boolean>(false);
  const [processedPorts, setProcessedPorts] = useState<PortActionSuccess[]>([]);
  const [failedPorts, setFailedPorts] = useState<PortActionFailure[]>([]);
  const showWaitBlock = isJuniper || isMeraki;

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setDialogState('default');
        setLoadingText(DEFAULT_LOADING_TEXT);
        setErrorText(DEFAULT_ERROR_TEXT);
        setReloadOnClose(false);
        setProcessedPorts([]);
        setFailedPorts([]);
      }, 100);
    }
  }, [open]);

  const resolveBatchDialogState = (
    succeeded: PortActionSuccess[],
    failed: PortActionFailure[]
  ): PortActionDialogState => {
    const hasSuccesses = succeeded.length > 0;
    const hasFailures = failed.length > 0;

    if (hasSuccesses && !hasFailures) return 'success';
    if (!hasSuccesses && hasFailures) return 'error';
    if (hasSuccesses && hasFailures) return 'partial';
    return 'error';
  };

  const shouldReloadOnClose = (state: PortActionDialogState): boolean =>
    state === 'success' || state === 'partial';

  const withBatchDialogStateHandling = async (
    fn: () => Promise<PortActionBatchResponse>
  ): Promise<void> => {
    try {
      const { succeeded, failed } = await fn();

      setProcessedPorts(succeeded);
      setFailedPorts(failed);

      const state = resolveBatchDialogState(succeeded, failed);
      setDialogState(state);
      setReloadOnClose(shouldReloadOnClose(state));
    } catch (error: any) {
      setDialogState('error');
      setReloadOnClose(false);
      setErrorText(parseApiError(error));
    }
  };

  const withDialogStateHandling = async (fn: () => Promise<AxiosResponse<any>>): Promise<void> => {
    try {
      await fn();
      setDialogState('success');
      setReloadOnClose(true);
    } catch (error: any) {
      setDialogState('error');
      setReloadOnClose(false);

      if (error.response?.data?.detail?.error_code) {
        const error_code: PortActionError = error.response.data.detail.error_code;
        setErrorText(portActionErrorDesc[error_code]);
      } else {
        setErrorText(parseApiError(error));
      }
    }
  };

  const performClassicPortBounce = async () => {
    const ports: PortDataType[] = selectedPorts.map(({ port, status, vlan, description }) => ({
      port,
      status,
      vlan,
      description,
    }));
    const payload: PerformPortBounceRequestType = {
      ip_address: ip_address,
      device_type: device_type,
      region: region,
      building_code: building_code,
      unit: unit,
      unit_name: unit_name,
      api_mode: isApiMode,
      ports: ports,
    };

    const getApiCall = () => {
      if (isDistro) {
        return () => ApiInstance.performPortBounceMacMini(payload);
      }
      if (unlimitedPortBounce) {
        return () => ApiInstance.performPortBounce(payload);
      }
      return () => ApiInstance.performLimitedPortBounce(payload);
    };

    await withDialogStateHandling(getApiCall());
  };

  const performMerakiPortBounce = async () => {
    const ports: MerakiPortDataType[] = selectedPorts.map(
      ({
        port_id,
        port,
        status,
        vlan,
        switch_serial,
        switch_name,
        switch_mac_address,
        description,
      }) => ({
        port_id,
        port,
        status,
        vlan,
        switch_serial,
        switch_name,
        switch_mac_address,
        description,
      })
    );

    const payload: PerformMerakiPortBounceRequestType = {
      ip_address: ip_address,
      device_type: device_type,
      region: region,
      building_code: building_code,
      unit: unit,
      unit_name: unit_name,
      api_mode: isApiMode,
      ports: ports,
    };
    const apiCall = unlimitedPortBounce
      ? () => ApiInstance.performMerakiPortBounce(payload)
      : () => ApiInstance.performLimitedMerakiPortBounce(payload);

    await withBatchDialogStateHandling(apiCall);
  };

  const handleConfirm = async () => {
    setDialogState('loading');
    setLoadingText('Please wait while we process your request...');

    if (isMeraki) {
      await performMerakiPortBounce();
    } else {
      await performClassicPortBounce();
    }
  };

  const handleClose = () => {
    if (reloadOnClose) {
      window.location.reload();
    } else {
      onClose();
    }
  };

  const getDialogTitle = () => titleMap[dialogState];
  const getTitleBackgroundColor = () => backgroundColorMap[dialogState];

  const getDialogContent = () => {
    switch (dialogState) {
      case 'loading':
        return <DialogContentLoading loadingText={loadingText} />;
      case 'success':
        return <DialogContentSuccess waitBlock={showWaitBlock} />;
      case 'partial':
        return (
          <DialogContentBatchResult
            waitBlock={showWaitBlock}
            succeeded={processedPorts}
            failed={failedPorts}
          />
        );
      case 'error':
        return <DialogContentError errorText={errorText} />;
      default:
        return <PortBounceForm selectedPorts={selectedPorts} />;
    }
  };

  const showCloseIcon = dialogState !== 'loading';
  const showDialogActions = dialogState === 'default';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={dialogStyles.dialog}
      sx={dialogStyles.dialogPaper}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-content"
    >
      <DialogTitle
        id="dialog-title"
        sx={{
          ...dialogStyles.dialogTitle,
          backgroundColor: getTitleBackgroundColor(),
        }}
      >
        {getDialogTitle()}
        {showCloseIcon && (
          <IconButton aria-label="close" onClick={handleClose} sx={dialogStyles.dialogCloseButton}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent id="dialog-content" sx={dialogStyles.dialogContent}>
        {getDialogContent()}
      </DialogContent>
      {showDialogActions && (
        <DialogActions sx={dialogStyles.dialogActions}>
          <Button
            onClick={handleClose}
            color="secondary"
            variant="contained"
            sx={dialogStyles.cancelButtonStyles}
          >
            <CloseIcon sx={dialogStyles.buttonIconSpacing} />
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            sx={dialogStyles.confirmButtonStyles}
          >
            <CheckIcon sx={dialogStyles.buttonIconSpacing} />
            Confirm
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PortBounceAction;
